<div class="verification-container">
  <!-- Toast Notification -->
  <div *ngIf="showToast" [ngClass]="'toast-' + toastType" class="toast-notification">
    <span class="toast-icon">{{ toastType === 'success' ? '✓' : '✕' }}</span>
    <span class="toast-message">{{ toastMessage }}</span>
  </div>

  <!-- Initial Loading State -->
  <div *ngIf="isLoadingInitial" class="loader-wrapper">
    <div class="loader-container">
      <div class="spinner"></div>
      <p class="loader-text">Loading...</p>
    </div>
  </div>

  <!-- Error Section - Show when link is invalid or expired -->
  <div *ngIf="!isLoadingInitial && showErrorSection" class="error-wrapper">
    <div class="error-card">
      <h1 class="error-title">Link Expired or Invalid</h1>
      <p class="error-message">The verification link you used is no longer valid or has expired.</p>
      <div class="error-details">
        <p class="error-detail-text">This could happen if:</p>
        <ul class="error-list">
          <li>The link has expired</li>
          <li>The link has already been used</li>
          <li>The link was incorrectly copied or modified</li>
        </ul>
      </div>
      <div class="error-actions">
        <p class="error-contact">Please contact the administrator to request a new verification link.</p>
      </div>
    </div>
  </div>

  <!-- Success Section - Show after documents submitted -->
  <div *ngIf="!isLoadingInitial && !showErrorSection && showSuccessSection" class="success-wrapper">
    <div class="success-card">
      <div class="success-icon">✓</div>
      <h1 class="success-title">Documents Submitted Successfully!</h1>
      <p class="success-message">Your documents have been received and are being reviewed</p>
    </div>
  </div>

  <!-- OTP Section - Initially visible -->
  <div *ngIf="!isLoadingInitial && !showErrorSection && showOtpSection" class="otp-wrapper">
    <div class="otp-card">
      <div class="header-section">
        <h1 class="main-title">Email Verification</h1>
        <p class="subtitle">Please verify your email to continue</p>
      </div>
      
      <div class="otp-content">
        <div class="email-section">
          <label for="email" class="section-label">
            <i class="email-icon">📧</i>
            Email Address
          </label>
          <div class="input-group">
            <input 
              type="email" 
              id="email" 
              [(ngModel)]="email" 
              disabled
              placeholder="Enter your email address"
              class="email-input">
            <button 
              type="button" 
              (click)="getOtp()" 
              [disabled]="!email || isLoadingOtp || isOtpSent"
              class="get-otp-btn">
              <i *ngIf="!isLoadingOtp" class="send-icon">📤</i>
              <i *ngIf="isLoadingOtp" class="loading-icon">⏳</i>
              <span *ngIf="!isLoadingOtp && !isOtpSent">Get OTP</span>
              <span *ngIf="isLoadingOtp">Sending...</span>
              <span *ngIf="isOtpSent && !isLoadingOtp">OTP Sent ✓</span>
            </button>
          </div>
        </div>

        <div class="otp-input-section" [class.enabled]="isOtpSent">
          <label for="otp" class="section-label">
            <i class="otp-icon">🔐</i>
            Enter Verification Code
          </label>
          <div class="otp-container">
            <input 
              type="text" 
              id="otp" 
              [(ngModel)]="otp" 
              (input)="onOtpInput($event)"
              [disabled]="!isOtpSent"
              placeholder="Enter 6-digit OTP"
              maxlength="6"
              class="otp-input">
            <div class="otp-info">
              <small *ngIf="!isOtpSent">Please send OTP first</small>
              <small *ngIf="isOtpSent">Enter the 6-digit code sent to your email</small>
            </div>
          </div>
          
          <div class="otp-actions">
            <button 
              type="button" 
              (click)="submitOtp()" 
              [disabled]="!otp || otp.length < 4 || isVerifyingOtp || !isOtpSent"
              class="submit-otp-btn">
              <i *ngIf="!isVerifyingOtp" class="verify-icon">✅</i>
              <i *ngIf="isVerifyingOtp" class="loading-icon">⏳</i>
              <span *ngIf="!isVerifyingOtp">Verify & Continue</span>
              <span *ngIf="isVerifyingOtp">Verifying...</span>
            </button>
          </div>

          <div *ngIf="isOtpSent" class="resend-section">
            <div class="resend-info">
              <small *ngIf="!canResendOtp" class="resend-timer">
                Resend OTP in <strong>{{ otpTimeRemaining }}s</strong>
              </small>
              <button 
                *ngIf="canResendOtp"
                type="button" 
                (click)="resendOtp()" 
                [disabled]="isLoadingOtp"
                class="resend-otp-btn">
                <i *ngIf="!isLoadingOtp" class="resend-icon">🔄</i>
                <i *ngIf="isLoadingOtp" class="loading-icon">⏳</i>
                <span>Resend OTP</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Document Checklist Section - Show after OTP verification -->
  <div *ngIf="!isLoadingInitial && !showErrorSection && showDocumentSection" class="document-wrapper">
    <div class="document-card">
      <div class="document-header">
        <div class="header-content">
          <h1 class="main-title">Document Checklist</h1>
          <p class="subtitle">Please bring the following documents</p>
          <div class="email-info">
            <span class="email-label">Verified Email:</span>
            <span class="email-value">{{email}}</span>
          </div>
        </div>
      </div>

      <div class="document-content">
        <!-- Loading State -->
        <div *ngIf="isLoadingDocuments" class="loading-container">
          <div class="loading-spinner">⏳</div>
          <p class="loading-text">Loading document fields...</p>
        </div>

        <!-- Document List -->
        <div *ngIf="!isLoadingDocuments" class="document-list">
          <div *ngFor="let document of documents; let i = index" class="document-item">
            <div class="document-main-row">
              <div class="checkbox-wrapper">
                <input 
                  type="checkbox" 
                  [id]="document.id"
                  [(ngModel)]="document.checked"
                  (change)="onDocumentChange(document)"
                  [disabled]="document.verified"
                  class="document-checkbox">
              </div>
              <div class="document-info">
                <label [for]="document.id" class="document-label">
                  <span class="document-number">{{i + 1}}.</span>
                  {{document.name}}
                </label>
              </div>
            </div>

            <!-- Upload Section - Only show when checked and type is upload -->
            <div *ngIf="document.checked && document.type === 'upload'" class="upload-section">
              <div class="upload-controls">
                <input 
                  type="file" 
                  [id]="document.id + '_file'"
                  (change)="onFileUpload($event, document)"
                  [disabled]="document.verified"
                  class="file-input"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
                <label [for]="document.id + '_file'" class="file-upload-btn" [class.disabled]="document.verified">
                  <i class="upload-icon">📁</i>
                  <span>{{ document.verified ? 'Verified' : 'Choose Files' }}</span>
                </label>
                <span class="upload-hint" *ngIf="!document.verified">PDF, Images, Word documents accepted</span>
                <span class="upload-hint verified-hint" *ngIf="document.verified">✓ This document has been verified</span>
              </div>
              
              <!-- Uploaded Files List -->
              <div *ngIf="document.uploadedFiles && document.uploadedFiles.length > 0" class="uploaded-files">
                <h5 class="files-title">Uploaded Files ({{document.uploadedFiles.length}})</h5>
                <div *ngFor="let file of document.uploadedFiles" class="file-item">
                  <div class="file-info">
                    <i class="file-icon">📄</i>
                    <a 
                      *ngIf="file.url" 
                      [href]="getSafeUrl(file.url)" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      class="file-name file-link"
                      title="Open file in new tab">
                      {{file.name}}
                    </a>
                    <span *ngIf="!file.url" class="file-name">{{file.name}}</span>
                  </div>
                  <button 
                    type="button" 
                    (click)="removeFile(document, file.id)"
                    [disabled]="document.verified"
                    class="delete-btn"
                    [class.disabled]="document.verified"
                    title="Remove file">
                    <mat-icon class="delete-icon">close</mat-icon>
                  </button>
                </div>
              </div>
            </div>

            <!-- Input Section - Only show when checked and type is input -->
            <div *ngIf="document.checked && document.type === 'input'" class="input-section">
              <div class="input-wrapper">
                <input 
                  type="text" 
                  [(ngModel)]="document.inputValue"
                  (input)="onInputChange(document)"
                  [disabled]="document.verified"
                  placeholder="Enter details..."
                  class="document-input">
                <i class="input-icon verified-icon" *ngIf="document.verified">✓</i>
              </div>
            </div>
          </div>
        </div>

        <div class="notes-section">
          <div class="notes-header">
            <h4><i class="notes-icon">📝</i> Additional Notes</h4>
          </div>
          <div class="notes-content">
            <p class="notes-instruction">Please open an Account with TD & send me the direct deposit form for your monthly proposal payment.</p>
            <textarea 
              [(ngModel)]="additionalNotes"
              class="notes-textarea" 
              placeholder="Add any additional notes or comments..."
              rows="4"></textarea>
          </div>
        </div>

        <div class="action-section">
          <div class="action-buttons">
            <button 
              type="button" 
              (click)="submitDocuments()" 
              [disabled]="isSubmittingDocuments"
              class="submit-documents-btn">
              <i *ngIf="!isSubmittingDocuments" class="submit-icon">📋</i>
              <i *ngIf="isSubmittingDocuments" class="loading-icon">⏳</i>
              <span *ngIf="!isSubmittingDocuments">Submit Documents</span>
              <span *ngIf="isSubmittingDocuments">Submitting...</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
